import {
  type ExcludeStrict,
  type ExtractStrict,
  type Merge,
  ReactUtils,
} from '@festina-life/core'
import cx from 'classnames'
import type { ComponentProps } from 'react'
import { Button } from 'react-aria-components'

import { Tooltip } from '../../feedback/tooltip/Tooltip'
import {
  type PropsWithSafeExtra,
  safeRestProps,
} from '../../libs/safeRestProps'
import { Icon } from '../../structure/icon/Icon'
import { ButtonLoadingContent } from '../button/ButtonLoadingContent'
import { buttonStyles } from '../button/buttonStyles'

type IconProps = ComponentProps<typeof Icon>
type ButtonStyleProps = Parameters<typeof buttonStyles>[0]

type IconButtonProps = Merge<
  PropsWithSafeExtra<
    typeof Button,
    'slot',
    {
      /**
       * Button type
       * @defaultValue `'button'`
       */
      type?: 'button' | 'submit' | 'reset'
      /**
       * ClassName and onClick can be migrated to be picked from react-aria-components Button if/when we migrate our Button.tsx to also use that. For now they are manually typed for better interop.
       */
      className?: string
      onClick?: ComponentProps<'button'>['onClick']
      /**
       * Used for toggle buttons
       */
      isSelected?: boolean
      isLoading?: boolean
      /**
       * Disabled the button
       */
      disabled?: ButtonStyleProps['disabled']
      variant?: ExcludeStrict<ButtonStyleProps['variant'], 'text'>
      isDanger?: ButtonStyleProps['isDanger']
      tip: ComponentProps<typeof Tooltip>['tip']
      iconName: ComponentProps<typeof Icon>['name']
      iconSize?: ExtractStrict<IconProps['size'], 'sm' | 'md'>
    }
  >,
  {
    slot?: 'previous' | 'next' | 'start' | 'end' // Slightly stricter typing to prevent typos on consumption. react-aria-components types it as string
  }
>

/**
 * Component for wrapping an icon in a button, with support for loading state and tooltips.
 */
export const IconButton = ({
  className,
  iconName,
  iconSize,
  isDanger = false,
  isSelected, // deliberately undefined by default to differentiate from false
  type = 'button',
  disabled = false,
  isLoading = false,
  tip,
  variant = 'discreet',
  extraProps = {},
  ref,
  ...restProps // Needed since we can pass other props like aria-*, data-* etc. or spread that isn't type checked
}: IconButtonProps) => {
  const isDisabled = disabled || isLoading
  const isSelectable = typeof isSelected === 'boolean'

  return (
    <ReactUtils.ConditionalWrap
      condition={!!tip}
      wrap={(children) => (
        <Tooltip tip={tip} ref={ref}>
          {/**
           * Tooltips for a disabled button don't work since
           * the button doesn't receive pointer events, so we
           * wrap the children in a span to ensure the tooltip
           * works even when the button is disabled.
           */}
          {isDisabled ? (
            <span
              tabIndex={0}
              className="inline-flex" // Aligned with the button's display style
            >
              {children}
            </span>
          ) : (
            children
          )}
        </Tooltip>
      )}
    >
      <Button
        ref={ref}
        {...safeRestProps(extraProps, restProps)}
        isDisabled={isDisabled}
        type={type}
        className={cx(
          'p-1',
          isSelectable && !isSelected
            ? cx(
                'relative inline-flex items-center justify-center hover:bg-current/10 active:bg-current/20',
                'rounded-lg', // needed to ensure hover bg is rounded too since it comes from button styles for other cases
              )
            : buttonStyles({
                disabled: isDisabled,
                isDanger,
                isFullWidth: false,
                variant,
                hasCustomPadding: true,
              }),
          className,
        )}
      >
        <ButtonLoadingContent isLoading={isLoading}>
          <Icon
            name={iconName}
            size={iconSize}
            color={isSelectable && !isSelected ? 'default' : 'unset'}
          />
        </ButtonLoadingContent>
      </Button>
    </ReactUtils.ConditionalWrap>
  )
}
